/**
 * Phase 22 iter_173 — Founding 100 claim page.
 * URL: /founder/claim/:token
 *
 * Login-gated. Verifies the invitation belongs to the caller, then
 * kicks off the $99 one-time Stripe checkout with the invitation
 * token in metadata so the webhook can atomically assign the next
 * Founder number.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SharedNavigation from "@/components/SharedNavigation";
import api from "@/lib/api";
import { toast } from "sonner";

const FounderClaimPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/memberships/invitation/${token}`);
      setInv(r.data?.invitation || null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "not_your_invitation") {
        setError("This invitation isn't for your account. Please sign in with the invited email.");
      } else if (detail === "invitation_not_found") {
        setError("This invitation link is invalid or was revoked.");
      } else if (e?.response?.status === 401) {
        navigate("/login?redirect=" + encodeURIComponent(window.location.pathname));
      } else {
        setError("We couldn't load this invitation. Try again shortly.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { load(); }, [load]);

  const claim = async () => {
    setBusy(true);
    try {
      const r = await api.post("/memberships/checkout", {
        plan_id: "founding_artist",
        invitation_token: token,
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "memberships_disabled") {
        toast.error("Founder claiming is temporarily paused. Please check back soon.");
      } else if (detail === "founding_100_capped") {
        toast.error("The Founding 100 is full. You'll be first in line for future Founder cohorts.");
      } else if (detail === "invitation_invalid_or_used") {
        toast.error("This invitation was already claimed or has expired.");
        load();
      } else {
        toast.error(detail || "Couldn't start checkout");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="founder-claim-page">
      <SharedNavigation />
      <div className="max-w-2xl mx-auto px-4 py-10">
        {loading && <p className="text-center text-gray-400" data-testid="founder-claim-loading">Loading invitation…</p>}
        {error && (
          <div className="rounded-2xl border border-rose-500/40 bg-rose-500/[0.05] p-6 text-center" data-testid="founder-claim-error">
            <p className="text-4xl mb-2">🚫</p>
            <p className="text-white font-black">{error}</p>
          </div>
        )}
        {inv && (
          <div
            className={`rounded-2xl border-2 p-6 sm:p-8 space-y-5 ${
              inv.status === "pending"
                ? "border-amber-500/50 bg-gradient-to-br from-amber-900/25 via-purple-900/20 to-black"
                : "border-white/10 bg-white/[0.03]"
            }`}
            data-testid={`founder-claim-card-${inv.status}`}
          >
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-black">
                Founding 100 · Invitation
              </p>
              <h1 className="text-3xl sm:text-4xl font-black mt-2">🏆 You're invited</h1>
              <p className="text-gray-200 mt-3 text-sm sm:text-base">
                You've been personally selected to join the Founding 100 —
                the first permanent Founding Artists of RGM. Claim your Founder
                number and lock in lifetime access.
              </p>
            </div>

            <ul className="space-y-2 text-sm text-gray-100">
              <li>🥇 Permanent <strong>Founding Artist #001–#100</strong> badge (never reused)</li>
              <li>🌟 "Founding Artist Since 2026" label on your profile</li>
              <li>🏛 Reserved spot in the Founding 100 Hall of Champions</li>
              <li>♾ Lifetime access · every future premium tier unlocked</li>
              <li>💎 One-time $99 payment (via secure Stripe checkout)</li>
            </ul>

            {inv.status === "pending" ? (
              <button
                onClick={claim}
                disabled={busy}
                className="w-full px-4 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-black font-black text-base"
                data-testid="founder-claim-cta"
              >
                {busy ? "Redirecting to secure checkout…" : "Claim my Founder number — $99"}
              </button>
            ) : (
              <p className="text-center text-sm text-gray-400" data-testid="founder-claim-status-note">
                Status: <span className="text-white font-black">{inv.status}</span>. Contact
                RGM if you believe this is a mistake.
              </p>
            )}
            {inv.expires_at && (
              <p className="text-[11px] text-center text-gray-500">
                Expires: {new Date(inv.expires_at).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FounderClaimPage;
